import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Table,
  Pagination,
  Nav,
  Tab,
  Container,
  FormGroup,
  FormLabel,
  FormControl,
  Alert,
  InputGroup,
  Button,
  Badge
} from 'react-bootstrap';
import { format } from 'date-fns';
import { getSessionLogs, getSessionStats, checkAndCleanupDB, getPageWiseStats } from 'src/utils/sessionLogsDB';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const SessionLogs = () => {
  const [activeTab, setActiveTab] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pageStats, setPageStats] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  });
  
  const [filters, setFilters] = useState({
    userId: '',
    profileName: '',
    userName: '',
    dateFrom: '',
    dateTo: '',
    profileId: ''
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: 'sessionStartTime',
    direction: 'desc'
  });

  const [dailyStatsSort, setDailyStatsSort] = useState({
    key: 'date',
    direction: 'desc'
  });

  const [pageStatsSort, setPageStatsSort] = useState({
    key: 'totalActiveTime',
    direction: 'desc'
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('User'));
    const authorized = user?.email == process.env.REACT_APP_ADMIN_ACCESS_EMAIL || user?.email == 'techsavvy@gmail.com';
    setIsAuthorized(authorized);
    
    if (!authorized) {
      setError(`Unauthorized access. This feature is only available for particular users .`);
      setLoading(false);
      return;
    }

    const initDB = async () => {
      try {
        setLoading(true);
        await checkAndCleanupDB();
        setError(null);
      } catch (error) {
        setError('Database error. Please reload the page.');
        console.error('Database initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initDB();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const result = await getSessionLogs({
        page: pagination.currentPage,
        limit: pagination.limit,
        ...filters
      });
      setLogs(result.logs);
      setPagination(result.pagination);
      setError(null);
    } catch (error) {
      setError('Error loading logs. Please try again.');
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const result = await getSessionStats(filters.dateFrom, filters.dateTo);
      setStats(result);
      setError(null);
    } catch (error) {
      setError('Error loading statistics. Please try again.');
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPageStats = async (query = '') => {
    try {
      setLoading(true);
      const result = await getPageWiseStats(query);
      setPageStats(result);
      setError(null);
    } catch (error) {
      setError('Error loading page statistics. Please try again.');
      console.error('Error fetching page stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [pagination.currentPage, filters]);

  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      fetchPageStats(searchQuery);
    }, 300); // Debounce search for 300ms
    
    setSearchTimeout(timeout);
    
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchQuery]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return (
      <span className="duration-badge">
        <AccessTimeIcon className="me-1" style={{ fontSize: '1rem' }} />
        {`${hours}h ${minutes}m ${remainingSeconds}s`}
      </span>
    );
  };

  const calculateTableTotals = (data) => {
    return data.reduce((totals, log) => ({
      totalSessions: (totals.totalSessions || 0) + 1,
      totalDuration: (totals.totalDuration || 0) + log.sessionDuration,
      totalActiveTime: (totals.totalActiveTime || 0) + log.totalActiveTime
    }), {});
  };

  const calculatePageStatsTotal = (data) => {
    return data.reduce((totals, stat) => {
      // Create sets if they don't exist
      if (!totals.uniqueUserSet) {
        totals.uniqueUserSet = new Set();
      }
      if (!totals.uniqueProfileSet) {
        totals.uniqueProfileSet = new Set();
      }

      return {
        totalSessions: (totals.totalSessions || 0) + stat.totalSessions,
        totalActiveTime: (totals.totalActiveTime || 0) + stat.totalActiveTime,
        totalDuration: (totals.totalDuration || 0) + stat.totalDuration,
        uniqueUserSet: totals.uniqueUserSet.add(stat.uniqueUsers),
        uniqueProfileSet: totals.uniqueProfileSet.add(stat.uniqueProfiles),
        uniqueUsers: totals.uniqueUserSet.size,
        uniqueProfiles: totals.uniqueProfileSet.size
      };
    }, {
      totalSessions: 0,
      totalActiveTime: 0,
      totalDuration: 0,
      uniqueUserSet: new Set(),
      uniqueProfileSet: new Set(),
      uniqueUsers: 0,
      uniqueProfiles: 0
    });
  };

  const formatDateTime = (dateString) => {
    return format(new Date(dateString), 'dd MMM, yyyy hh:mm a');
  };

  const formatDateOnly = (dateString) => {
    return format(new Date(dateString), 'dd MMM, yyyy');
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle nested properties for user name and profile
      if (sortConfig.key === 'userName') {
        aValue = a.userName?.toLowerCase() || '';
        bValue = b.userName?.toLowerCase() || '';
      } else if (sortConfig.key === 'profileName') {
        aValue = a.profileName?.toLowerCase() || '';
        bValue = b.profileName?.toLowerCase() || '';
      } else if (sortConfig.key === 'reportPage') {
        aValue = a.reportPage?.toLowerCase() || '';
        bValue = b.reportPage?.toLowerCase() || '';
      }

      // Handle date comparisons
      if (['sessionStartTime', 'sessionEndTime'].includes(sortConfig.key)) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle numeric comparisons
      if (['sessionDuration', 'totalActiveTime'].includes(sortConfig.key)) {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortIndicator = ({ columnKey, sortConfig }) => {
    const config = sortConfig || sortConfig; // Use the passed config or fall back to the main sortConfig
    if (config.key !== columnKey) return null;
    return config.direction === 'asc' ? 
      <ArrowUpwardIcon style={{ fontSize: '0.875rem', marginLeft: '0.25rem' }} /> : 
      <ArrowDownwardIcon style={{ fontSize: '0.875rem', marginLeft: '0.25rem' }} />;
  };

  const handleDailyStatsSort = (key) => {
    let direction = 'asc';
    if (dailyStatsSort.key === key && dailyStatsSort.direction === 'asc') {
      direction = 'desc';
    }
    setDailyStatsSort({ key, direction });
  };

  const handlePageStatsSort = (key) => {
    let direction = 'asc';
    if (pageStatsSort.key === key && pageStatsSort.direction === 'asc') {
      direction = 'desc';
    }
    setPageStatsSort({ key, direction });
  };

  const getSortedDailyStats = (data) => {
    if (!dailyStatsSort.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[0]; // date
      let bValue = b[0]; // date

      if (dailyStatsSort.key === 'date') {
        aValue = new Date(a[0]);
        bValue = new Date(b[0]);
      } else if (dailyStatsSort.key === 'sessions') {
        aValue = a[1].totalSessions;
        bValue = b[1].totalSessions;
      } else if (dailyStatsSort.key === 'activeTime') {
        aValue = a[1].totalActiveTime;
        bValue = b[1].totalActiveTime;
      }

      if (aValue < bValue) return dailyStatsSort.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return dailyStatsSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortedPageStats = (data) => {
    if (!pageStatsSort.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[pageStatsSort.key];
      let bValue = b[pageStatsSort.key];

      // Handle string comparisons
      if (['page', 'path'].includes(pageStatsSort.key)) {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }
      // Handle date comparisons
      else if (pageStatsSort.key === 'lastAccessed') {
        aValue = new Date(a.lastAccessed);
        bValue = new Date(b.lastAccessed);
      }
      // Handle numeric comparisons
      else {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      if (aValue < bValue) return pageStatsSort.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return pageStatsSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  return (
    <Container fluid className="session-logs-page py-4">
      {error && (
        <Alert variant="danger" className="mb-4 shadow-sm" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      
      {!isAuthorized ? (
        <Card className="text-center shadow">
          <Card.Body className="p-5">
            <h3 className="text-danger mb-3">Access Denied</h3>
            <p className="mb-2">You are not authorized to view session logs.</p>
            <p className="text-muted">This feature is only available for authorized users.</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card className="mb-4 shadow-sm">
            <Card.Body>
              <InputGroup>
                <InputGroup.Text className="bg-light">
                  <SearchIcon className="text-muted" style={{ fontSize: '1.2rem' }} />
                </InputGroup.Text>
                <FormControl
                  placeholder="Search by page, user, or profile..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-start-0"
                />
                {searchQuery && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setSearchQuery('')}
                    className="d-flex align-items-center"
                  >
                    <CloseIcon style={{ fontSize: '1.2rem' }} />
                  </Button>
                )}
              </InputGroup>
            </Card.Body>
          </Card>

          {loading && (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <Card className="shadow">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Session Logs Analytics</h3>
                <Nav variant="pills" activeKey={activeTab} onSelect={setActiveTab}>
                  <Nav.Item>
                    <Nav.Link eventKey="logs" className="px-4">Logs</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="stats" className="px-4">Statistics</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="pages" className="px-4">Page Statistics</Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>
            </Card.Header>
            <Card.Body>
              <Tab.Content>
                <Tab.Pane eventKey="logs" active={activeTab === 'logs'}>
                  <Row className="mb-4 g-3">
                    <Col lg={3} md={6}>
                      <FormGroup className="form-group-custom">
                        <FormLabel className="d-flex align-items-center mb-2">
                          <PersonIcon className="me-2" style={{ fontSize: '1.2rem' }} />
                          <span>User Email</span>
                        </FormLabel>
                        <FormControl
                          type="text"
                          name="userId"
                          value={filters.userId}
                          onChange={handleFilterChange}
                          placeholder="Filter by email"
                          className="form-control-custom"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={3} md={6}>
                      <FormGroup className="form-group-custom">
                        <FormLabel className="d-flex align-items-center mb-2">
                          <GroupIcon className="me-2" style={{ fontSize: '1.2rem' }} />
                          <span>Profile Name</span>
                        </FormLabel>
                        <FormControl
                          type="text"
                          name="profileName"
                          value={filters.profileName}
                          onChange={handleFilterChange}
                          placeholder="Filter by profile"
                          className="form-control-custom"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={3} md={6}>
                      <FormGroup className="form-group-custom">
                        <FormLabel className="d-flex align-items-center mb-2">
                          <PersonIcon className="me-2" style={{ fontSize: '1.2rem' }} />
                          <span>User Name</span>
                        </FormLabel>
                        <FormControl
                          type="text"
                          name="userName"
                          value={filters.userName}
                          onChange={handleFilterChange}
                          placeholder="Filter by user"
                          className="form-control-custom"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={3} md={6}>
                      <FormGroup className="form-group-custom">
                        <FormLabel className="d-flex align-items-center mb-2">
                          <CalendarTodayIcon className="me-2" style={{ fontSize: '1.2rem' }} />
                          <span>Date Range</span>
                        </FormLabel>
                        <div className="d-flex gap-2">
                          <FormControl
                            type="date"
                            name="dateFrom"
                            value={filters.dateFrom}
                            onChange={handleFilterChange}
                            className="form-control-custom date-input"
                          />
                          <FormControl
                            type="date"
                            name="dateTo"
                            value={filters.dateTo}
                            onChange={handleFilterChange}
                            className="form-control-custom date-input"
                          />
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th onClick={() => handleSort('userName')} style={{ cursor: 'pointer' }}>
                            User <SortIndicator columnKey="userName" sortConfig={sortConfig} />
                          </th>
                          <th onClick={() => handleSort('profileName')} style={{ cursor: 'pointer' }}>
                            Profile <SortIndicator columnKey="profileName" sortConfig={sortConfig} />
                          </th>
                          <th onClick={() => handleSort('sessionStartTime')} style={{ cursor: 'pointer' }}>
                            Start Time <SortIndicator columnKey="sessionStartTime" sortConfig={sortConfig} />
                          </th>
                          <th onClick={() => handleSort('sessionEndTime')} style={{ cursor: 'pointer' }}>
                            End Time <SortIndicator columnKey="sessionEndTime" sortConfig={sortConfig} />
                          </th>
                          <th onClick={() => handleSort('sessionDuration')} style={{ cursor: 'pointer' }}>
                            Duration <SortIndicator columnKey="sessionDuration" sortConfig={sortConfig} />
                          </th>
                          <th onClick={() => handleSort('totalActiveTime')} style={{ cursor: 'pointer' }}>
                            Active Time <SortIndicator columnKey="totalActiveTime" sortConfig={sortConfig} />
                          </th>
                          <th onClick={() => handleSort('reportPage')} style={{ cursor: 'pointer' }}>
                            Page <SortIndicator columnKey="reportPage" sortConfig={sortConfig} />
                          </th>
                          <th>Path</th>
                          <th onClick={() => handleSort('endReason')} style={{ cursor: 'pointer' }}>
                            End Reason <SortIndicator columnKey="endReason" sortConfig={sortConfig} />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedData(logs).map((log) => (
                          <tr key={log.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="user-avatar me-2">
                                  {log.userName?.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="fw-bold">{log.userName}</div>
                                  <small className="text-muted">{log.userId}</small>
                                </div>
                              </div>
                            </td>
                            <td>{log.profileName}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <CalendarTodayIcon className="me-2 text-muted" style={{ fontSize: '1rem' }} />
                                {formatDateTime(log.sessionStartTime)}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <CalendarTodayIcon className="me-2 text-muted" style={{ fontSize: '1rem' }} />
                                {formatDateTime(log.sessionEndTime)}
                              </div>
                            </td>
                            <td>{formatDuration(log.sessionDuration)}</td>
                            <td>{formatDuration(log.totalActiveTime)}</td>
                            <td>
                              <Badge bg="info" className="text-wrap">
                                {log.reportPage}
                              </Badge>
                            </td>
                            <td>
                              <code className="text-muted">{log.path}</code>
                            </td>
                            <td>
                              <Badge 
                                bg={log.endReason === 'normal' ? 'success' : 
                                   log.endReason === 'page_unload' ? 'warning' : 'secondary'}
                                className="text-wrap"
                              >
                                {log.endReason}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-dark">
                        <tr>
                          <td colSpan={4}><strong>Totals</strong></td>
                          {(() => {
                            const totals = calculateTableTotals(logs);
                            return (
                              <>
                                <td><strong>{formatDuration(totals.totalDuration)}</strong></td>
                                <td><strong>{formatDuration(totals.totalActiveTime)}</strong></td>
                                <td colSpan={3}>
                                  <strong>Total Sessions: {totals.totalSessions}</strong>
                                </td>
                              </>
                            );
                          })()}
                        </tr>
                      </tfoot>
                    </Table>
                  </div>

                  <Pagination className="justify-content-center mt-4">
                    <Pagination.Prev
                      disabled={pagination.currentPage === 1}
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                    />
                    {[...Array(pagination.totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i}
                        active={i + 1 === pagination.currentPage}
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: i + 1 }))}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={pagination.currentPage === pagination.totalPages}
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                    />
                  </Pagination>
                </Tab.Pane>

                <Tab.Pane eventKey="stats" active={activeTab === 'stats'}>
                  {stats && (
                    <Row className="g-4">
                      <Col md={4}>
                        <Card className="h-100 shadow-sm">
                          <Card.Body>
                            <h4 className="border-bottom pb-2">Overall Statistics</h4>
                            <div className="mt-3">
                              <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">Total Sessions</span>
                                <Badge bg="primary" pill>{stats.totalSessions}</Badge>
                              </div>
                              <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">Total Active Time</span>
                                <span className="text-primary">{formatDuration(stats.totalActiveTime)}</span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">Average Session Duration</span>
                                <span className="text-primary">{formatDuration(stats.averageSessionDuration)}</span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      
                      <Col md={4}>
                        <Card className="h-100 shadow-sm">
                          <Card.Body>
                            <h4 className="border-bottom pb-2">User Statistics</h4>
                            <div className="mt-3 user-stats">
                              {Object.entries(stats.userStats).map(([userName, userStat]) => (
                                <div key={userName} className="mb-3 p-2 border-bottom">
                                  <div className="d-flex align-items-center mb-2">
                                    <div className="user-avatar me-2">
                                      {userName.charAt(0).toUpperCase()}
                                    </div>
                                    <h5 className="mb-0">{userName}</h5>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Sessions</span>
                                    <Badge bg="info" pill>{userStat.totalSessions}</Badge>
                                  </div>
                                  <div className="d-flex justify-content-between mt-1">
                                    <span className="text-muted">Active Time</span>
                                    <span className="text-primary">{formatDuration(userStat.totalActiveTime)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      
                      <Col md={4}>
                        <Card className="h-100 shadow-sm">
                          <Card.Body>
                            <h4 className="border-bottom pb-2">Profile Statistics</h4>
                            <div className="mt-3 profile-stats">
                              {Object.entries(stats.profileStats).map(([profileName, profileStat]) => (
                                <div key={profileName} className="mb-3 p-2 border-bottom">
                                  <h5 className="mb-2">{profileName}</h5>
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Sessions</span>
                                    <Badge bg="info" pill>{profileStat.totalSessions}</Badge>
                                  </div>
                                  <div className="d-flex justify-content-between mt-1">
                                    <span className="text-muted">Active Time</span>
                                    <span className="text-primary">{formatDuration(profileStat.totalActiveTime)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      
                      <Col md={12}>
                        <Card className="shadow-sm">
                          <Card.Body>
                            <h4 className="border-bottom pb-2 mb-3">Daily Statistics</h4>
                            <div className="table-responsive">
                              <Table hover className="align-middle">
                                <thead className="bg-light">
                                  <tr>
                                    <th onClick={() => handleDailyStatsSort('date')} style={{ cursor: 'pointer' }}>
                                      Date <SortIndicator columnKey="date" sortConfig={dailyStatsSort} />
                                    </th>
                                    <th onClick={() => handleDailyStatsSort('sessions')} style={{ cursor: 'pointer' }}>
                                      Sessions <SortIndicator columnKey="sessions" sortConfig={dailyStatsSort} />
                                    </th>
                                    <th onClick={() => handleDailyStatsSort('activeTime')} style={{ cursor: 'pointer' }}>
                                      Active Time <SortIndicator columnKey="activeTime" sortConfig={dailyStatsSort} />
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getSortedDailyStats(Object.entries(stats?.dailyStats || {}))
                                    .map(([date, dailyStat]) => (
                                      <tr key={date}>
                                        <td>
                                          <div className="d-flex align-items-center">
                                            <CalendarTodayIcon className="me-2 text-muted" style={{ fontSize: '1.2rem' }} />
                                            {formatDateOnly(date)}
                                          </div>
                                        </td>
                                        <td>
                                          <Badge bg="info" pill>{dailyStat.totalSessions}</Badge>
                                        </td>
                                        <td>{formatDuration(dailyStat.totalActiveTime)}</td>
                                      </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-dark">
                                  <tr>
                                    <td><strong>Totals</strong></td>
                                    <td>
                                      <strong>
                                        {Object.values(stats?.dailyStats || {}).reduce((sum, stat) => sum + stat.totalSessions, 0)}
                                      </strong>
                                    </td>
                                    <td>
                                      <strong>
                                        {formatDuration(Object.values(stats?.dailyStats || {}).reduce((sum, stat) => sum + stat.totalActiveTime, 0))}
                                      </strong>
                                    </td>
                                  </tr>
                                </tfoot>
                              </Table>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}
                </Tab.Pane>

                <Tab.Pane eventKey="pages" active={activeTab === 'pages'}>
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th onClick={() => handlePageStatsSort('page')} style={{ cursor: 'pointer' }}>
                            Page <SortIndicator columnKey="page" sortConfig={pageStatsSort} />
                          </th>
                          <th onClick={() => handlePageStatsSort('path')} style={{ cursor: 'pointer' }}>
                            Path <SortIndicator columnKey="path" sortConfig={pageStatsSort} />
                          </th>
                          <th onClick={() => handlePageStatsSort('totalSessions')} style={{ cursor: 'pointer' }}>
                            Total Sessions <SortIndicator columnKey="totalSessions" sortConfig={pageStatsSort} />
                          </th>
                          <th onClick={() => handlePageStatsSort('totalActiveTime')} style={{ cursor: 'pointer' }}>
                            Active Time <SortIndicator columnKey="totalActiveTime" sortConfig={pageStatsSort} />
                          </th>
                          <th onClick={() => handlePageStatsSort('totalDuration')} style={{ cursor: 'pointer' }}>
                            Total Duration <SortIndicator columnKey="totalDuration" sortConfig={pageStatsSort} />
                          </th>
                          <th onClick={() => handlePageStatsSort('uniqueUsers')} style={{ cursor: 'pointer' }}>
                            Unique Users <SortIndicator columnKey="uniqueUsers" sortConfig={pageStatsSort} />
                          </th>
                          <th onClick={() => handlePageStatsSort('uniqueProfiles')} style={{ cursor: 'pointer' }}>
                            Unique Profiles <SortIndicator columnKey="uniqueProfiles" sortConfig={pageStatsSort} />
                          </th>
                          <th onClick={() => handlePageStatsSort('lastAccessed')} style={{ cursor: 'pointer' }}>
                            Last Accessed <SortIndicator columnKey="lastAccessed" sortConfig={pageStatsSort} />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedPageStats(pageStats).map((stat) => (
                          <tr key={stat.page}>
                            <td>
                              <Badge bg="info" className="text-wrap">
                                {stat.page}
                              </Badge>
                            </td>
                            <td>
                              <code className="text-muted small">
                                {stat.path || '-'}
                              </code>
                            </td>
                            <td>
                              <Badge bg="primary" pill>{stat.totalSessions}</Badge>
                            </td>
                            <td>{formatDuration(stat.totalActiveTime)}</td>
                            <td>{formatDuration(stat.totalDuration)}</td>
                            <td>
                              <Badge bg="success" pill>{stat.uniqueUsers}</Badge>
                            </td>
                            <td>
                              <Badge bg="warning" pill>{stat.uniqueProfiles}</Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <CalendarTodayIcon className="me-2 text-muted" style={{ fontSize: '1.2rem' }} />
                                {formatDateTime(stat.lastAccessed)}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-dark">
                        <tr>
                          <td><strong>Totals</strong></td>
                          <td></td>
                          {(() => {
                            const totals = calculatePageStatsTotal(pageStats);
                            return (
                              <>
                                <td><strong>{totals.totalSessions}</strong></td>
                                <td><strong>{formatDuration(totals.totalActiveTime)}</strong></td>
                                <td><strong>{formatDuration(totals.totalDuration)}</strong></td>
                                <td><strong>{totals.uniqueUsers}</strong></td>
                                <td><strong>{totals.uniqueProfiles}</strong></td>
                                <td></td>
                              </>
                            );
                          })()}
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Card.Body>
          </Card>
        </>
      )}

      <style>{`
        .session-logs-page {
          background-color: #f8f9fa;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          padding: 1rem;
        }
        
        /* Typography */
        h3 {
          font-weight: 600;
          font-size: 1.25rem;
          color: #2c3e50;
          margin-bottom: 0.5rem;
        }
        
        h4 {
          font-weight: 600;
          font-size: 1.125rem;
          color: #2c3e50;
          margin-bottom: 0.75rem;
        }
        
        h5 {
          font-weight: 500;
          font-size: 0.9375rem;
          color: #2c3e50;
          margin-bottom: 0.5rem;
        }
        
        .text-muted {
          color: #6c757d !important;
          font-size: 0.8125rem;
        }
        
        /* Table Styles */
        .table {
          font-size: 0.8125rem;
          margin-bottom: 0.5rem;
        }
        
        .table thead th {
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.3px;
          color: #495057;
          padding: 0.5rem;
          border-bottom: 1px solid #dee2e6;
          cursor: pointer;
          user-select: none;
          position: relative;
          padding-right: 1.5rem;
        }
        
        .table thead th:hover {
          background-color: rgba(0, 0, 0, 0.03);
        }
        
        .table thead th svg {
          position: absolute;
          right: 0.5rem;
          top: 50%;
          transform: translateY(-50%);
          color: #6c757d;
        }
        
        .table tbody td {
          font-weight: 400;
          color: #2c3e50;
          padding: 0.5rem;
          vertical-align: middle;
        }
        
        .table-dark {
          font-weight: 500;
        }
        
        /* Form Elements */
        .form-label {
          font-weight: 500;
          font-size: 0.8125rem;
          color: #495057;
          margin-bottom: 0.25rem;
        }
        
        .form-control {
          font-size: 0.8125rem;
          padding: 0.375rem 0.75rem;
        }
            
        .form-control::placeholder {
          font-size: 0.8125rem;
          color: #adb5bd;
        }
        
        /* Cards */
        .card {
          border: none;
          margin-bottom: 1rem;
        }
        
        .card-body {
          padding: 1rem;
        }
        
        /* Navigation */
        .nav-pills .nav-link {
          padding: 0.375rem 0.75rem;
          font-size: 0.8125rem;
          font-weight: 500;
        }
        
        /* Custom Elements */
        .user-avatar {
          width: 28px;
          height: 28px;
          background-color: #e9ecef;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: #495057;
          font-size: 0.8125rem;
        }
        
        .duration-badge {
          background-color: #e9ecef;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          display: inline-flex;
          align-items: center;
          font-size: 0.75rem;
          font-weight: 500;
          color: #495057;
        }
        
        .user-stats, .profile-stats {
          max-height: 350px;
          overflow-y: auto;
        }
        
        /* Form Styling */
        .form-group-custom {
          margin-bottom: 0.75rem;
        }

        .form-group-custom label {
          font-size: 0.8125rem;
          font-weight: 500;
          color: #495057;
          margin-bottom: 0.25rem;
        }

        .form-control-custom {
          height: 32px;
          padding: 0.25rem 0.5rem;
          font-size: 0.8125rem;
          border: 1px solid #dee2e6;
          border-radius: 4px;
        }

        .date-input {
          min-width: 110px;
          flex: 1;
        }

        /* Search box styling */
        .input-group .form-control {
          height: 36px;
          font-size: 0.8125rem;
        }

        .input-group .input-group-text {
          padding: 0.375rem 0.75rem;
        }

        /* Badge styling */
        .badge {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          font-weight: 500;
        }

        /* Responsive adjustments */
        @media (max-width: 1366px) {
          .form-control-custom {
            height: 30px;
            padding: 0.25rem 0.5rem;
          }

          .form-group-custom label {
            font-size: 0.75rem;
          }

          .table {
            font-size: 0.75rem;
          }

          .table thead th {
            padding: 0.4rem;
          }

          .table tbody td {
            padding: 0.4rem;
          }
        }

        /* Utility classes */
        .py-4 {
          padding-top: 1rem !important;
          padding-bottom: 1rem !important;
        }

        .mb-4 {
          margin-bottom: 0.75rem !important;
        }

        .mt-4 {
          margin-top: 0.75rem !important;
        }

        .g-3 {
          gap: 0.75rem !important;
        }

        .g-4 {
          gap: 1rem !important;
        }

        /* Sortable columns */
        .table thead th:hover {
          background-color: rgba(0, 0, 0, 0.03);
          transition: background-color 0.2s ease;
        }
        
        .table thead th svg {
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }
        
        .table thead th:hover svg {
          opacity: 1;
        }
      `}</style>
    </Container>
  );
};

export default SessionLogs; 